<script setup>
import { computed, inject, ref } from "vue";
import { format } from "date-fns";
import ResourceTableBodyRow from "@Components/Table/ResourceTableBodyRow.vue";
import ResourceTableBodyColumn from "@Components/Table/ResourceTableBodyColumn.vue";
import { getStatus } from "@Utils/publication";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { ArrowPathIcon, EyeIcon, PencilIcon, PlayIcon, TrashIcon, QueueListIcon, ArrowDownTrayIcon } from "@heroicons/vue/20/solid";
import { QueueListIcon as QueueListIconOutline } from "@heroicons/vue/24/outline";
import { useForm } from "@/form";
import Tooltip from "@Components/Tooltip.vue";
import Logs from "@Components/Logs/Logs.vue";
import { useI18n } from "vue-i18n";
import { DestroyModal, Modal } from "@Components/Modal";
import { FormCheckbox } from "@Components/Form";
import { PrimaryButton, SecondaryButton } from "@Components/Button";
import { MagicIcon } from "@Icons/index";

const props = defineProps(["publication", "publications", "project", "modelValue"]);
const emit = defineEmits(["updated", "update:modelValue"]);

const $notify = inject("$notify");
const { t: __ } = useI18n();

const publishForm = useForm({
    publication: props.publication.id,
});

const retryForm = useForm({
    publication: props.publication.id,
});

const regenerateForm = useForm({
    publication: props.publication.id,
});

const destroyForm = useForm({
    publication: props.publication.id,
});

const destroyModal = ref();
const logsModalOpen = ref(false);

const publish = () => {
    if (publishForm.processing) return;
    publishForm
        .post("publications_publish")
        .then(() => {
            $notify({
                title: __("La publication #{id} sera publiée prochainement.", {
                    id: publishForm.publication,
                }),
                type: "success",
            });

            emit("updated");
        })
        .catch(() => {
            $notify({
                title: "An error occurred while publishing.",
                type: "error",
            });
        });
};

const retry = () => {
    if (retryForm.processing) return;
    retryForm
        .post("publications_retry")
        .then(() => {
            $notify({
                title: __("La publication #{id} sera publiée prochainement.", {
                    id: publishForm.publication,
                }),
                type: "success",
            });

            emit("updated");
        })
        .catch(() => {
            $notify({
                title: "An error occurred while publishing.",
                type: "error",
            });
        });
};

const regenerate = () => {
    if (regenerateForm.processing) return;
    regenerateForm
        .post("publications_regenerate")
        .then(() => {
            $notify({
                title: __("La publication #{id} sera publiée prochainement.", {
                    id: publishForm.publication,
                }),
                type: "success",
            });

            emit("updated");
        })
        .catch(() => {
            $notify({
                title: "An error occurred while publishing.",
                type: "error",
            });
        });
};

const destroy = () => {
    if (destroyForm.processing) return;
    destroyForm
        .post("publications_destroy")
        .then(() => {
            destroyModal.value.hide();

            $notify({
                title: __("La publication #{id} a été supprimée.", {
                    id: publishForm.publication,
                }),
                type: "success",
            });

            emit("updated");
        })
        .catch(() => {
            $notify({
                title: "An error occurred while deleting.",
                type: "error",
            });
        });
};

const isSelected = computed(() => {
    return props.modelValue.selected.includes(props.publication.id);
});

const toggleSelect = () => {
    if (isSelected.value) {
        props.modelValue.selected.splice(props.modelValue.selected.indexOf(props.publication.id), 1);
        props.modelValue.all = false;
    } else {
        props.modelValue.selected.push(props.publication.id);
    }

    props.modelValue.current = props.modelValue.selected.length === props.publications.data.length;
};

const downloadLogs = () => {
    let lines = [];

    props.publication.logs.forEach((log) => {
        lines.push(format(new Date(log.created_at), "yyyy-MM-dd HH:mm:ss") + " - " + log.message);
    });

    const url = window.URL.createObjectURL(
        new Blob([lines.join("\n")], {
            type: "text/plain;charset=utf-8",
        })
    );
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = "publication-" + props.publication.id + ".log";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
};

const analyseLogsProcessing = ref(false);
const analyseLogsModalRef = ref();
const analyseLogsResponse = ref(null);

const analyseLogs = async () => {
    if (analyseLogsProcessing.value) {
        return;
    }

    analyseLogsProcessing.value = true;

    analyseLogsResponse.value = null;

    const response = await useForm({
        logs: props.publication.logs,
        language: props.project.language.key,
    }).post("analyse_logs");

    console.log(response);

    analyseLogsResponse.value = response;
    logsModalOpen.value = false;
    analyseLogsModalRef.value.show();

    analyseLogsProcessing.value = false;
};
</script>

<template>
    <ResourceTableBodyRow>
        <ResourceTableBodyColumn class="pl-4 lg:pl-8 sm:pl-6">
            <FormCheckbox :modelValue="isSelected" @update:modelValue="toggleSelect" />
        </ResourceTableBodyColumn>
        <ResourceTableBodyColumn class="max-w-0">
            <div class="flex flex-col gap-y-1">
                <div class="text-sm font-medium leading-6 text-gray-900 truncate dark:text-white">
                    {{ publication.title }}
                </div>
                <div v-if="['rss', 'url', 'sitemap'].indexOf(project.type) !== -1" class="mt-0.5 text-xs text-gray-900 truncate dark:text-white">
                    <span v-if="publication.meta.url">{{ publication.meta.url }}</span>
                    <span v-else class="italic">{{ __("Aucune URL fournie") }}</span>
                </div>
                <div class="mt-0.5 text-xs text-gray-600 dark:text-gray-400">#{{ publication.id }}</div>
            </div>
        </ResourceTableBodyColumn>
        <ResourceTableBodyColumn>
            <span :class="[getStatus(publication.status)?.classes, 'inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ring-1 ring-inset']">
                {{ __(getStatus(publication.status)?.name) }}
            </span>
        </ResourceTableBodyColumn>
        <ResourceTableBodyColumn class="hidden text-sm text-gray-900 dark:text-white lg:table-cell">
            {{ format(new Date(publication.published_at), "yyyy-MM-dd HH:mm") }}
        </ResourceTableBodyColumn>
        <ResourceTableBodyColumn class="pr-4 text-right lg:pr-8 sm:pr-6">
            <div class="flex justify-end items-center space-x-1.5">
                <Tooltip v-if="publication.status === 'failed'" :content="__('Réessayer')">
                    <button
                        @click="retry(publication)"
                        class="flex items-center text-gray-600 bg-white rounded-full dark:text-gray-400 dark:bg-gray-950 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                        <ArrowPathIcon class="w-5 h-5" aria-hidden="true" />
                    </button>
                </Tooltip>
                <Tooltip v-if="publication.status === 'success'" :content="__('Regénérer')">
                    <button
                        @click="regenerate(publication)"
                        class="flex items-center text-gray-600 bg-white rounded-full dark:text-gray-400 dark:bg-gray-950 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                        <ArrowPathIcon class="w-5 h-5" aria-hidden="true" />
                    </button>
                </Tooltip>
                <Tooltip v-if="publication.status === 'idle' && !publication.is_next_published" :content="__('Publier maintenant')">
                    <button
                        @click="publish(publication)"
                        class="flex items-center text-gray-600 bg-white rounded-full dark:text-gray-400 dark:bg-gray-950 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                        <PlayIcon class="w-5 h-5" aria-hidden="true" />
                    </button>
                </Tooltip>
                <Tooltip v-if="publication.post_id" :content="__('Voir le post')">
                    <a
                        :href="publication.display_link"
                        target="_blank"
                        class="flex items-center text-gray-600 bg-white rounded-full dark:text-gray-400 dark:bg-gray-950 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                        <EyeIcon class="w-5 h-5" aria-hidden="true" />
                    </a>
                </Tooltip>
                <Tooltip v-if="publication.post_id" :content="__('Modifier le post')">
                    <a
                        :href="publication.edit_link"
                        target="_blank"
                        class="flex items-center text-gray-600 bg-white rounded-full dark:text-gray-400 dark:bg-gray-950 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                        <PencilIcon class="w-5 h-5" aria-hidden="true" />
                    </a>
                </Tooltip>
                <Tooltip :content="__('Logs')">
                    <button
                        @click="logsModalOpen = true"
                        v-if="publication.logs"
                        class="relative flex items-center text-gray-600 bg-white rounded-full dark:text-gray-400 dark:bg-gray-950 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                        <QueueListIcon class="w-5 h-5" aria-hidden="true" />
                        <div class="-mr-1 -mt-1 absolute top-0 right-0" v-if="publication.status === 'failed'">
                            <span class="w-2 h-2 relative flex">
                                <span class="w-full h-full absolute inline-flex bg-red-400 rounded-full opacity-75 animate-ping"></span>
                                <span class="w-2 h-2 relative inline-flex bg-red-500 rounded-full"></span>
                            </span>
                        </div>
                    </button>
                </Tooltip>
                <Tooltip :content="__('Supprimer')">
                    <button
                        @click="destroyModal.show()"
                        class="flex items-center text-gray-600 bg-white rounded-full dark:text-gray-400 dark:bg-gray-950 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                        <TrashIcon class="w-5 h-5" aria-hidden="true" />
                    </button>
                </Tooltip>
            </div>

            <TransitionRoot as="template" :show="logsModalOpen">
                <Dialog as="div" class="z-[100000] relative" @close="logsModalOpen = false">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0">
                        <div class="bg-opacity-75 fixed inset-0 bg-gray-900 transition-opacity" />
                    </TransitionChild>

                    <div class="z-[100000] overflow-y-auto fixed inset-0">
                        <div class="min-h-full flex justify-center items-end p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild
                                as="template"
                                enter="ease-out duration-300"
                                enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enter-to="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leave-from="opacity-100 translate-y-0 sm:scale-100"
                                leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel
                                    class="transform w-full overflow-hidden relative px-4 pt-5 pb-4 text-left bg-gray-800 rounded-lg border border-gray-700 shadow-xl transition-all lg:max-w-3xl sm:max-w-lg sm:p-6 sm:my-8">
                                    <div class="sm:flex sm:items-start">
                                        <div
                                            class="flex-shrink-0 mx-auto w-12 h-12 flex justify-center items-center bg-gray-700 rounded-full sm:w-10 sm:h-10 sm:mx-0">
                                            <QueueListIconOutline class="w-6 h-6 text-gray-200" aria-hidden="true" />
                                        </div>
                                        <div class="flex-grow mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <div class="flex justify-between items-center space-x-3">
                                                <DialogTitle as="h3" class="text-base font-semibold leading-6 text-white"
                                                    >Publication #{{ destroyForm.publication }} logs</DialogTitle
                                                >
                                                <Tooltip :content="__('Télécharger')">
                                                    <button
                                                        @click="downloadLogs(publication.logs)"
                                                        class="flex items-center text-gray-400 rounded-full focus-visible:outline-2 focus-visible:outline-gray-200 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-200">
                                                        <ArrowDownTrayIcon class="w-5 h-5" aria-hidden="true" />
                                                    </button>
                                                </Tooltip>
                                            </div>
                                            <Logs class="mt-3" :logs="publication.logs" />
                                            <div v-if="publication.status === 'failed'" class="flex justify-center mt-6">
                                                <SecondaryButton @click="analyseLogs" :icon="MagicIcon" :processing="analyseLogsProcessing" :z="3">
                                                    {{ __("Comprendre les erreurs") }}
                                                </SecondaryButton>
                                            </div>
                                        </div>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>

            <DestroyModal
                ref="destroyModal"
                @destroy="destroy"
                :title="__('Supprimer la publication #{id}', { id: destroyForm.publication })"
                :description="
                    __('Êtes-vous sûr de vouloir supprimer la publication #{id} ? Cette action ne peut pas être annulée.', { id: destroyForm.publication })
                " />

            <Modal ref="analyseLogsModalRef">
                <div class="p-6 whitespace-break-spaces dark:text-white">
                    {{ analyseLogsResponse }}
                </div>
            </Modal>
        </ResourceTableBodyColumn>
    </ResourceTableBodyRow>
</template>
